import React from 'react';
import { useSelector } from 'react-redux';
import { User, Stethoscope } from 'lucide-react';
import { RootState } from '../redux/store';

interface DetailItemProps {
  label: string;
  value: string;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
  <div>
    <span className="text-xs text-gray-500 block mb-0.5">{label}</span>
    <p className="font-medium text-gray-900 text-sm truncate">{value}</p>
  </div>
);

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => (
  <div className="flex-1 lg:min-w-0">
    <div className="flex items-center gap-2 mb-4">
      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-teamcare-yellow shadow-[2px_2px_0_0_rgba(0,0,0,1)] text-white border border-white">
        {icon}
      </span>
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    </div>
    <div className="space-y-3 pl-2">
      {children}
    </div>
  </div>
);

export const SmartPatientDetails: React.FC = () => {
  const { patientDetails, treatment, selectedDurationMinutes } = useSelector(
    (s: RootState) => s.appointment
  );

  const patientName = patientDetails.firstName 
    ? `${patientDetails.firstName} ${patientDetails.lastName || ''}`.trim()
    : 'N/A';

  const providerName = treatment?.provider
    ? `${treatment.provider.firstName} ${treatment.provider.lastName}`
    : 'N/A';

  const hasAppointmentDetails = treatment || selectedDurationMinutes;

  return (
    <div className="lg:mt-[67px]">
      <div className="bg-white border border-gray-200 rounded-lg p-4 pt-3 shadow-sm">
        <div className="flex flex-row lg:flex-col gap-6 lg:gap-4">
          {/* Patient Details Section */}
          <Section icon={<User className="h-4 w-4" />} title="Patient Details">
            <DetailItem label="Name" value={patientName} />
            <DetailItem label="Email" value={patientDetails.email || 'N/A'} />
            <DetailItem label="Phone" value={patientDetails.phone || 'N/A'} />
          </Section>

          {/* Appointment Details Section */}
          {hasAppointmentDetails && (
            <Section icon={<Stethoscope className="h-4 w-4" />} title="Appointment">
              {treatment && (
                <DetailItem label="Appointment Type" value={treatment.title || 'N/A'} />
              )}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartPatientDetails;